import { Sidebar } from "src/components/Sidebar/Sidebar"
import { ProjectImage } from "src/components/ProjectImage/ProjectImage"
import "./styles.scss"

const MainContent = () => {
    return (
        <div className="main-content p-4">
            <div className="header mb-4">
                <h1 className="title">Kick the Tires</h1>
                <h2 className="subtitle">Full Stack SaaS - AI-Powered Pre-Purchase Inspection Reports</h2>
            </div>

            <div className="description mb-5">
                <p>
                    Buying a used car is one of the largest purchases most people make, yet the process of vetting one is still largely manual, expensive, and opaque. Kick the Tires is a consumer-facing SaaS platform built to fix that. A user pastes in a vehicle listing URL or enters details manually, and within minutes receives a comprehensive Pre-Purchase Inspection (PPI) report delivered to their inbox as a PDF.
                </p>
                <p>
                    The report covers repair recommendations, common known issues for the model year, a predicted maintenance schedule, market pricing insights, CPO eligibility, and AI-driven image analysis. All of this is generated by running multiple AI models in parallel against the scraped listing data.
                </p>
            </div>

            <div className="project-screenshot">
                <ProjectImage
                    src="/assets/Screenshots/kickthetires.png"
                    alt="Kick the Tires Homepage"
                    description="Kick the Tires - paste a listing URL and get a full PPI report delivered to your inbox"
                />
            </div>

            <div className="description things-i-worked-on mb-5">
                <h4>Things I Worked On</h4>
                <p>I was the lead developer on the project, working alongside our CEO and another developer. My main areas of ownership were:</p>
                <ul>
                    <li><a href="#scraping" className="work-link">Web Scraping</a></li>
                    <li><a href="#ai-pipeline" className="work-link">Parallel AI Analysis Pipeline</a></li>
                    <li><a href="#backend" className="work-link">Backend Architecture</a></li>
                    <li><a href="#report" className="work-link">PDF Report Generation & Delivery</a></li>
                    <li><a href="#frontend" className="work-link">Frontend</a></li>
                </ul>
            </div>

            <div id="scraping" className="description mb-5">
                <h4>Web Scraping</h4>
                <p>
                    Getting reliable vehicle data out of listing sites is harder than it sounds. Sites like Facebook Marketplace and CarGurus go out of their way to block scrapers, so I built a tiered scraping strategy that escalates gracefully rather than failing outright.
                </p>
                <p>
                    The first attempt is a lightweight headless Firefox crawl using <a href="https://crawl4ai.com/" target="_blank" rel="noopener noreferrer">Crawl4AI</a>, an async Playwright-based crawler that concatenates webpage content into a compact format, which helps reduce token usage when passing the scraped data to Gemini. If that gets blocked, the system falls back to a stealth Chromium setup with rotating proxies. As a last resort, a manual image and data upload flow surfaces to the user so the report can still be generated even when scraping fails completely.
                </p>
                <p>
                    Once the raw HTML is retrieved, I pass it through Google Gemini using structured tool-calling to extract the vehicle fields like Make, Model, Year, VIN, Price, mileage, listing images, and seller comments.
                </p>
                <ul>
                    <li>Crawl4AI (async Playwright / Firefox headless) for standard listing sites</li>
                    <li>Playwright Stealth with rotating proxies for anti-bot bypass</li>
                    <li>Selenium / PyVirtualDisplay for Linux headless environments in Cloud Run</li>
                    <li>Gemini structured tool-calling for post-scrape field extraction</li>
                </ul>
            </div>

            <div id="ai-pipeline" className="description mb-5">
                <h4>Parallel AI Analysis Pipeline</h4>
                <p>
                    The core of the product is a multi-step AI pipeline. The goal was to run everything that could be parallelized concurrently, then chain the steps that depended on earlier outputs.
                </p>
                <p>
                    In practice, an analysis run fires 4-5 independent Gemini calls at the same time: listing categorization, flag and highlight identification, common known issues for the model year, maintenance schedule, CPO eligibility, and per-image damage analysis. Once those complete, their outputs feed into the downstream steps like repair recommendations and market pricing insights.
                </p>
                <ProjectImage
                    src="/assets/Screenshots/Kickthetiresmarketanal.png"
                    alt="Market analysis results"
                    description="Market analysis results - suggested asking price, avg sold price, and factors impacting value"
                    width="90%"
                />
                <p>
                    Every AI response is constrained by a JSON schema via Vertex AI Function Calling, and a secondary Gemini validation pass catches and corrects any garbled or truncated outputs before they get written to Firestore. This two-pass pattern was key to keeping structured outputs reliable.
                </p>
                <ul>
                    <li>Google Gemini 3.0 Pro & Flash via Vertex AI with enforced JSON schemas</li>
                    <li><code>concurrent.futures.ThreadPoolExecutor</code> for parallel AI step execution</li>
                    <li>Secondary Gemini validation pass on all structured outputs</li>
                    <li>NHTSA vPIC API for recall lookups by VIN via Gemini function-calling</li>
                    <li>VINAudit API for vehicle history reports</li>
                </ul>
            </div>

            <div id="backend" className="description mb-5">
                <h4>Backend</h4>
                <p>
                    The backend is a Python / Flask application organized with Blueprint-based routing, separating concerns into scraper, analysis, PPI, research, payment, and auth route groups. Gunicorn serves as the production WSGI server, with Quart handling the async Cloud Run task workers.
                </p>
                <p>
                    One of the more interesting decisions was using Google Cloud Tasks to offload long-running work from the main request handlers. Things like image downloading, per-image AI classification, and VINAudit report fetching all run as background tasks. In development, these run as async threads using <code>asyncio</code> event loops on daemon threads so the local experience mirrors production.
                </p>
                <p>
                    Real-time progress updates are streamed to the frontend via Server-Sent Events (SSE) using Flask's <code>stream_with_context</code>, so users can watch the report build step-by-step rather than staring at a spinner.
                </p>
                <ul>
                    <li>Python / Flask with Blueprint routing</li>
                    <li>Gunicorn (production) and Quart (async Cloud Run workers)</li>
                    <li>Google Cloud Tasks for async job dispatch</li>
                    <li>Server-Sent Events for real-time progress streaming</li>
                    <li>Firestore for all vehicle documents, analysis steps, and user data</li>
                    <li>Google Cloud Storage for scraped image ingestion and durable storage</li>
                    <li>Firebase Auth with custom JWT middleware on all protected routes</li>
                    <li>Stripe for payment processing</li>
                </ul>
            </div>

            <div id="report" className="description mb-5">
                <h4>PDF Report Generation & Delivery</h4>
                <p>
                    The finished report is rendered server-side using WeasyPrint. An HTML template gets populated with the structured AI outputs, then converted to a PDF. Postmark handles the transactional email delivery, attaching the PDF directly to the message so users get something they can save and bring to an independent mechanic.
                </p>
                <ProjectImage
                    src="/assets/Screenshots/kickthetirespdf.png"
                    alt="Generated PPI report PDF"
                    description="An example generated Pre-Purchase Inspection report, emailed directly to the user as a PDF"
                    width="90%"
                />
                <ul>
                    <li>WeasyPrint for server-side HTML-to-PDF generation</li>
                    <li>Postmark / SendGrid for transactional email delivery</li>
                </ul>
            </div>

            <div id="frontend" className="description mb-5">
                <h4>Frontend</h4>
                <p>
                    The frontend is a React 18 / TypeScript SPA using React Router v6 and Ant Design as the primary component library. Axios handles API requests, and the Firebase JS SDK manages auth state and Firestore subscriptions. The SSE progress stream from the backend is consumed directly by a React hook that drives the live status UI while the report generates.
                </p>
                <ProjectImage
                    src="/assets/Screenshots/kickthetiresreport.png"
                    alt="Kick the Tires report first page"
                    description="First page of the completed report shown in-app, with vehicle overview and highlights"
                    width="90%"
                />
                <ul>
                    <li>React 18 + TypeScript, React Router v6</li>
                    <li>Ant Design (antd 5) component library</li>
                    <li>Axios + Firebase JS SDK</li>
                    <li>Deployed to Firebase Hosting</li>
                </ul>
            </div>

            <div id="conclusion" className="description mb-5">
                <h4>Takeaways</h4>
                <p>
                    This was a really fun project to work on. Almost every layer of it had an interesting engineering problem, whether it was getting reliable data out of sites that actively try to block you, squeezing latency out of the AI pipeline, or keeping structured LLM outputs consistent enough to actually trust programmatically.
                </p>
                <p>
                    The parallel AI pipeline pattern in particular is something I have carried forward. Running concurrent AI steps, enforcing JSON schemas, and adding a validation pass turned out to be a solid template for getting predictable behavior out of LLMs in a production context.
                </p>
            </div>

        </div>
    )
}

interface ProjectLayoutProps {
    sidebar: React.ReactNode;
    children: React.ReactNode;
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ sidebar, children }) => (
    <div className="container-fluid p-0">
        <div className="row g-0">
            <div className="col-md-3">{sidebar}</div>
            <div className="col-md-9">{children}</div>
        </div>
    </div>
)

export default function KickTheTiresProject() {
    const sidebarProps = {
        toolUrl: "",
        disabledLink: true,
        responsibilities: [
            "Python Flask",
            "React / TypeScript",
            "Google Gemini / Vertex AI",
            "Crawl4AI / Playwright",
            "Google Cloud Tasks",
            "Cloud Run",
            "Firestore / GCS",
            "Stripe",
            "WeasyPrint",
            "Firebase Auth"
        ]
    }

    return (
        <ProjectLayout sidebar={<Sidebar {...sidebarProps} />}>
            <MainContent />
        </ProjectLayout>
    )
}
